import { schemaParts, type Schema, type Vec3 } from "./card";
import type { PoseMask } from "./pose";

// Hit reactions (spike 002, phase B; the squash-landing-tiers-and-directional-hit-reactions suggestion): a table of
// rules, as data, turns one resolved hit into one reaction: which figure reacts (the target, or the attacker for a
// parried blow), which pose plays on its layer (a flinch, a stagger, a knockback, a stun), how hard (its weight by
// severity), aimed at the blow (the pose turned so a reaction authored for a blow from the front leans away from the
// blow's real side) and how far the figure is knocked back. The first rule that matches wins. Driven by the game's
// hit event, not by combat code: combat says what happened, the table says how a body shows it.

/** What a reaction needs from a hit (the spike's `combat:hit` payload has these and more). */
export interface HitInfo {
  attacker: number;
  target: number;
  kind: string;
  /** 0 to 1. */
  severity: number;
  /** Unit, attacker to target (world). */
  dir: Readonly<Vec3>;
  outcome: string;
  unblockable?: boolean;
}

/** One rule of the table. */
export interface ReactionRule {
  id: string;
  /** Matches these hit kinds (absent: any). */
  kinds?: string[];
  /** Matches these outcomes (absent: any). */
  outcomes?: string[];
  /** Matches severities in [min, max] (absent: any). */
  severity?: [number, number];
  /** Who shows it: the hit's target, or its attacker (a parried attacker's stun). */
  who: "target" | "attacker";
  pose: string;
  /** Overrides the pose's own mask. */
  mask?: PoseMask;
  /** Turn the pose toward the blow (it is authored for a blow from the front). */
  directional?: boolean;
  /** Its weight at severity 0 and 1 (linear between). */
  weight: [number, number];
  /** Retime the whole pose to last this long (s): a stun as long as combat's. */
  seconds?: number;
  /** Knocked back along the blow: metres at severity 0 and 1, over `seconds`. */
  knockback?: { distance: [number, number]; seconds: number };
}

/** What a hit makes a body do. */
export interface Reaction {
  rule: string;
  /** The entity that reacts. */
  entity: number;
  pose: string;
  mask?: PoseMask;
  weight: number;
  /** The blow's travel yaw (world, radians: atan2(x, z) of `dir`) for a directional rule, else null. */
  blowYaw: number | null;
  seconds?: number;
  /** Where and how far the body is pushed, or null. */
  knockback: { dir: Vec3; distance: number; seconds: number } | null;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp01 = (u: number): number => Math.min(1, Math.max(0, u));

/** The reaction a hit calls for under `rules` (the first match), or null when none matches (a dodge). */
export function chooseReaction(rules: readonly ReactionRule[], hit: HitInfo): Reaction | null {
  const sev = clamp01(hit.severity);
  const rule = rules.find(
    (r) => (!r.kinds || r.kinds.includes(hit.kind)) && (!r.outcomes || r.outcomes.includes(hit.outcome)) && (!r.severity || (sev >= r.severity[0] && sev <= r.severity[1])),
  );
  if (!rule) return null;
  const flat = Math.hypot(hit.dir[0], hit.dir[2]);
  const along: Vec3 = flat > 1e-6 ? [hit.dir[0] / flat, 0, hit.dir[2] / flat] : [0, 0, 1];
  return {
    rule: rule.id,
    entity: rule.who === "attacker" ? hit.attacker : hit.target,
    pose: rule.pose,
    ...(rule.mask ? { mask: rule.mask } : {}),
    weight: lerp(rule.weight[0], rule.weight[1], sev),
    blowYaw: rule.directional && flat > 1e-6 ? Math.atan2(hit.dir[0], hit.dir[2]) : null,
    ...(rule.seconds !== undefined ? { seconds: rule.seconds } : {}),
    knockback: rule.knockback ? { dir: along, distance: lerp(rule.knockback.distance[0], rule.knockback.distance[1], sev), seconds: rule.knockback.seconds } : null,
  };
}

/** The pose-layer direction (`PoseEntry.dir`) for a blow travelling at world yaw `blowYaw` into a figure facing
 *  `figureYaw`: reactions are authored for a blow from the front, which travels toward the figure's back (local yaw π). */
export function reactionDir(blowYaw: number, figureYaw: number): number {
  const d = blowYaw - figureYaw - Math.PI;
  return Math.atan2(Math.sin(d), Math.cos(d));
}

const { num, obj, list } = schemaParts;

/** The reaction rule's JSON Schema; `extra` adds the game's fields. */
export function reactionRuleSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "ReactionRule",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...(extra.required ?? []), "id", "who", "pose", "weight"],
    properties: {
      ...(extra.properties ?? {}),
      id: { type: "string" },
      kinds: list({ type: "string" }, 1, 16),
      outcomes: list({ type: "string" }, 1, 8),
      severity: list(num(0, 1), 2, 2, "[min, max]"),
      who: { enum: ["target", "attacker"] },
      pose: { type: "string" },
      mask: { enum: ["full", "upper"] },
      directional: { type: "boolean" },
      weight: list(num(0, 1), 2, 2, "At severity 0 and 1"),
      seconds: num(0.05, 10, "Retime the pose to last this long"),
      knockback: obj({ distance: list(num(0, 10), 2, 2, "Metres at severity 0 and 1"), seconds: num(0.02, 3) }, ["distance", "seconds"]),
    },
  };
}
